<template>
  <Dialog v-model="visible" title="导入商城商品" width="900px">
    <el-alert
      title="导入后生成本店待审核的下架商品，库存为 0。请编辑库存及配送信息，审核通过后上架。原商城商品保留；再次导入会生成新商品。"
      type="info"
      :closable="false"
      class="mb-12px"
    />
    <el-form inline @submit.prevent="search">
      <el-form-item label="商品名称">
        <el-input v-model="query.name" clearable placeholder="搜索商城商品" @keyup.enter="search" />
      </el-form-item>
      <el-form-item><el-button @click="search">搜索</el-button></el-form-item>
    </el-form>
    <el-table v-loading="loading" :data="list" @selection-change="select">
      <el-table-column type="selection" width="50" />
      <el-table-column prop="id" label="编号" width="90" />
      <el-table-column label="图片" width="80">
        <template #default="{ row }"
          ><el-image :src="row.picUrl" class="h-48px w-48px" fit="cover"
        /></template>
      </el-table-column>
      <el-table-column prop="name" label="商品名称" min-width="200" />
      <el-table-column prop="shopId" label="来源店铺编号" width="120" />
      <el-table-column label="售价（元）" width="120">
        <template #default="{ row }">{{ fenToYuan(row.price) }}</template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
    <el-form v-if="needExpress" label-width="120px" class="mt-12px">
      <el-form-item label="本店运费模板" required>
        <el-select v-model="templateId" filterable placeholder="请选择本店运费模板">
          <el-option
            v-for="item in templates"
            :key="item.id"
            :value="item.id!"
            :label="item.name"
          />
        </el-select>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="saving" @click="visible = false">取消</el-button>
      <el-button
        type="primary"
        :loading="saving"
        :disabled="!selected.length || loading"
        @click="confirm"
      >
        导入选中商品（{{ selected.length }}）
      </el-button>
    </template>
  </Dialog>
</template>
<script setup lang="ts">
import * as ProductApi from '@/api/merchant/product'
import * as TemplateApi from '@/api/merchant/delivery/expressTemplate'
import { fenToYuan } from '@/utils'
const emit = defineEmits<{ success: [] }>()
const message = useMessage()
const visible = ref(false)
const loading = ref(false)
const saving = ref(false)
const total = ref(0)
const list = ref<ProductApi.MallImportProduct[]>([])
const selected = ref<ProductApi.MallImportProduct[]>([])
const templates = ref<TemplateApi.DeliveryExpressTemplateVO[]>([])
const templateId = ref<number>()
const query = reactive({ name: '', pageNo: 1, pageSize: 10 })
const needExpress = computed(() => selected.value.some((row) => row.deliveryTypes?.includes(1)))
const select = (rows: ProductApi.MallImportProduct[]) => {
  selected.value = rows
}
const load = async () => {
  loading.value = true
  selected.value = []
  try {
    const data = await ProductApi.getMallImportPage(query)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}
const search = () => {
  query.pageNo = 1
  return load()
}
const open = async () => {
  visible.value = true
  query.name = ''
  query.pageNo = 1
  templateId.value = undefined
  templates.value = []
  await load()
}
watch(needExpress, async (value) => {
  if (value && !templates.value.length) templates.value = await TemplateApi.getSimpleTemplateList()
})
const confirm = async () => {
  if (saving.value || !selected.value.length) return
  if (selected.value.length > 50) {
    message.warning('每次最多导入 50 件商品')
    return
  }
  if (needExpress.value && !templateId.value) {
    message.warning('请选择本店运费模板')
    return
  }
  saving.value = true
  try {
    const ids: number[] = await ProductApi.importMallProducts({
      spuIds: selected.value.map((row) => row.id),
      deliveryTemplateId: templateId.value
    })
    message.success(`已导入 ${ids.length} 件商品，请到仓库中编辑库存并等待审核`)
    visible.value = false
    emit('success')
  } finally {
    saving.value = false
  }
}
defineExpose({ open })
</script>
